/**
 * F-131 MyCalendarPage — week-view with WorkingHours bands + booked orders + blocked windows.
 *
 * Layout: 7 day columns × hourly rows (08-22 by default). Bands render as
 * coloured blocks. Empty hour → "Заблокувати" modal. Booked → navigate
 * to /orders/:id.
 */
import { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { Card, Button, Modal, Icon, EmptyState, ErrorState } from '@/components/atoms';
import { toast } from '@/components/atoms/Toast';
import { useBackButton } from '@/hooks';
import {
  fetchWorkingHours,
  fetchBlockedWindows,
  createBlockedWindow,
  type WorkingHours,
  type BlockedWindow,
} from '@/api/booking';
import { orders as ordersApi, specialists as specialistsApi } from '@/api';
// F-180 D8: PresenceSwitcher inline above the grid so the specialist can
// flip to "vacation" without bouncing back to /me.
import { PresenceSwitcher } from '@/components/domain';
import type { Order, SpecialistProfile } from '@/types/domain';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

const HOUR_START = 8;
const HOUR_END = 22;
const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];

export function MyCalendarPage(): JSX.Element {
  useBackButton();
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState<Dayjs>(() =>
    dayjs().startOf('week').add(1, 'day'),
  );
  const [workingHours, setWorkingHours] = useState<WorkingHours[]>([]);
  const [blocked, setBlocked] = useState<BlockedWindow[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [blockTarget, setBlockTarget] = useState<{ day: Dayjs; hour: number } | null>(null);
  const [reason, setReason] = useState('');
  // F-180 D8: cached profile for the PresenceSwitcher initial state.
  const [profile, setProfile] = useState<SpecialistProfile | null>(null);
  // F-180 A8: distinguish "still loading" / "fetch failed" / "no working
  // hours configured" so the grid stops rendering as a silent blank wall.
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>(
    'loading',
  );

  const reload = (): void => {
    setLoadState('loading');
    Promise.allSettled([
      fetchWorkingHours().then((res) => setWorkingHours(res.results)),
      fetchBlockedWindows().then((res) => setBlocked(res.results)),
      ordersApi.fetchUpcoming().then((res) => setOrders(res.results)),
      specialistsApi.fetchMySpecialist().then(setProfile),
    ]).then((results) => {
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        failed.forEach((r) =>
          logger.error('calendar fetch', (r as PromiseRejectedResult).reason),
        );
        setLoadState('error');
      } else {
        setLoadState('ready');
      }
    });
  };

  useEffect(reload, []);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day')),
    [weekStart],
  );

  const hours = useMemo(
    () => Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i),
    [],
  );

  function classifyCell(day: Dayjs, hour: number): {
    state: 'working' | 'blocked' | 'booked' | 'idle';
    order?: Order;
  } {
    const cellStart = day.hour(hour).minute(0).second(0);
    const cellEnd = cellStart.add(1, 'hour');
    // Booked order matches scheduled_at hour.
    const order = orders.find((o) => {
      if (!o.scheduled_at) return false;
      const at = dayjs(o.scheduled_at);
      return at.isSame(day, 'day') && at.hour() === hour;
    });
    if (order) return { state: 'booked', order };
    // Blocked?
    const isBlocked = blocked.some((bw) => {
      const bs = dayjs(bw.start_at);
      const be = dayjs(bw.end_at);
      return cellStart.isBefore(be) && cellEnd.isAfter(bs);
    });
    if (isBlocked) return { state: 'blocked' };
    // WorkingHours?
    const weekday = (day.day() + 6) % 7; // dayjs: Sun=0..Sat=6 → Mon=0..Sun=6
    const inWh = workingHours.some((wh) => {
      if (wh.weekday !== weekday || !wh.is_active) return false;
      const start = parseInt(wh.start_time.split(':')[0] ?? '0', 10);
      const end = parseInt(wh.end_time.split(':')[0] ?? '0', 10);
      return hour >= start && hour < end;
    });
    return { state: inWh ? 'working' : 'idle' };
  }

  async function confirmBlock(): Promise<void> {
    if (!blockTarget) return;
    const start = blockTarget.day.hour(blockTarget.hour).minute(0).second(0);
    const end = start.add(1, 'hour');
    try {
      await createBlockedWindow({
        start_at: start.toISOString(),
        end_at: end.toISOString(),
        reason,
      });
      toast.success('Час заблоковано');
      setBlockTarget(null);
      setReason('');
      reload();
    } catch (err) {
      logger.error('block window', err);
      toast.error('Не вдалось заблокувати');
    }
  }

  // F-180 A8 — short-circuit empty/error states so the grid never renders
  // as a silent blank wall. Empty `workingHours` is the most common cause
  // of confusion ("я відкрив календар і там нічого") so it gets a
  // dedicated nudge to /me/working-hours.
  if (loadState === 'error') {
    return (
      <Layout title="Календар">
        <ErrorState
          title="Не вдалось завантажити календар"
          message="Спробуй ще раз — можливо, проблема з мережею."
          onRetry={reload}
        />
      </Layout>
    );
  }
  if (loadState === 'ready' && workingHours.length === 0) {
    return (
      <Layout title="Календар">
        {profile ? (
          <div style={{ marginBottom: 12 }}>
            <PresenceSwitcher
              initial={{
                presence_status: profile.presence_status,
                presence_message: profile.presence_message ?? '',
              }}
              onChange={setProfile}
            />
          </div>
        ) : null}
        <EmptyState
          icon="calendar"
          title="Спочатку додай робочі години"
          message="Без них клієнти не бачать вільних слотів."
          action={{ label: 'Додати години', to: '/me/working-hours' }}
        />
      </Layout>
    );
  }

  return (
    <Layout title="Календар">
      {/* F-180 D8: inline presence so a specialist can flip to vacation
          without losing context. Renders only when we have the profile. */}
      {profile ? (
        <div style={{ marginBottom: 12 }}>
          <PresenceSwitcher
            initial={{
              presence_status: profile.presence_status,
              presence_message: profile.presence_message ?? '',
            }}
            onChange={setProfile}
          />
        </div>
      ) : null}
      <Card>
        <div className={styles.formRow}>
          <Button
            variant="secondary"
            onClick={() => setWeekStart(weekStart.subtract(7, 'day'))}
          >
            ←
          </Button>
          <strong style={{ flex: 1, textAlign: 'center' }}>
            {weekStart.format('D MMM')} — {weekStart.add(6, 'day').format('D MMM')}
          </strong>
          <Button
            variant="secondary"
            onClick={() => setWeekStart(weekStart.add(7, 'day'))}
          >
            →
          </Button>
        </div>
        <a
          href="/me/working-hours"
          onClick={(e) => {
            e.preventDefault();
            navigate('/me/working-hours');
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--accent, #2481cc)',
            marginTop: 8,
          }}
        >
          <Icon name="edit" size={14} />
          Робочі години
        </a>
      </Card>

      <Card>
        <div className={styles.calGrid}>
          <div className={styles.calCorner} />
          {days.map((d) => (
            <div key={d.toISOString()} className={styles.calDayHead}>
              <div className={styles.calDayLabel}>{WEEKDAY_LABELS[d.day() === 0 ? 6 : d.day() - 1]}</div>
              <div className={styles.calDayDate}>{d.format('D')}</div>
            </div>
          ))}
          {hours.map((h) => (
            <>
              <div key={`h${h}`} className={styles.calHourLabel}>
                {String(h).padStart(2, '0')}
              </div>
              {days.map((d) => {
                const cell = classifyCell(d, h);
                return (
                  <button
                    type="button"
                    key={`${d.toISOString()}-${h}`}
                    className={[
                      styles.calCell,
                      cell.state === 'working' ? styles.calCellWorking : '',
                      cell.state === 'blocked' ? styles.calCellBlocked : '',
                      cell.state === 'booked' ? styles.calCellBooked : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      if (cell.state === 'booked' && cell.order) {
                        navigate(`/orders/${cell.order.id}`);
                      } else if (cell.state !== 'blocked') {
                        setBlockTarget({ day: d, hour: h });
                      }
                    }}
                  >
                    {cell.state === 'booked' && cell.order ? '·' : ''}
                  </button>
                );
              })}
            </>
          ))}
        </div>
      </Card>

      {blockTarget && (
        <Modal open onClose={() => setBlockTarget(null)} title="Заблокувати час">
          <p>
            {blockTarget.day.format('D MMM')} о {String(blockTarget.hour).padStart(2, '0')}:00
          </p>
          <input
            type="text"
            placeholder="Причина (необов'язково)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ width: '100%', padding: 8, marginTop: 8 }}
          />
          <div className={styles.formRow} style={{ marginTop: 12 }}>
            <Button onClick={confirmBlock}>Заблокувати</Button>
            <Button variant="secondary" onClick={() => setBlockTarget(null)}>
              Скасувати
            </Button>
          </div>
        </Modal>
      )}
    </Layout>
  );
}
